import React, { Component } from 'react';
import { connect } from 'react-redux';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@/ui';
import { resetModal } from '../../actions';

const initialData = {
  isOpen: false,
  formValidationError: false,
  formErrorMessage: null,
  saveErrorMessage: null,
  saveError: false,
  saveWarning: false,
  saveWarningMessage: null,
};
const states = {
  open: 'OPEN',
  closed: 'CLOSED',
  saving: 'SAVING',
  updating: 'UPDATING',
  saveError: 'SAVE_ERROR',
  saveWarning: 'SAVE_WARNING',
  formValidationError: 'FORM_VALIDATION_ERROR',
};
/** Body layout shared by the normal, warning and error states. */
const bodyIconSx = (color) => ({ fontSize: '3rem', color, flex: '0 0 auto' });

class SaveEntityModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      state: states.closed,
      data: initialData,
    };
  }

  componentWillReceiveProps(nextProps, nextContext) {
    // toggle success state if stuck
    const isStuck = this.state.state === states.submitting;
    if (isStuck) {
      this.moveToState(states.closed);
    }

    // if form submission failed due to validation
    const errors = this.props.getErrors();
    if (errors.invalid) {
      this.moveToState(states.formValidationError);
    }
  }

  moveToState = (state, payload = {}) => {
    const { open, closed, saving, updating, saveError, saveWarning, formValidationError } = states;

    switch (state) {
      case open:
        this.setState((prevState) => ({
          state: open,
          data: { ...prevState.data, isOpen: true, formValidationError: false },
        }));
        break;
      case closed:
        setTimeout(() => {
          this.props.resetModal();
        }, 5000);
        this.setState((prevState) => ({
          state: closed,
          data: { ...prevState.data, ...initialData },
        }));
        break;
      case saving:
        const afterSetLocal = async () => {
          try {
            await this.props.onClickSubmit();
            const errors = this.props.getErrors();
            if (errors.saveError || errors._error) {
              this.moveToState(saveError, { saveError: errors.saveError, saveErrorMessage: errors._error });
            } else if (errors.saveWarning || errors._warning) {
              this.moveToState(saveWarning, { saveWarning: errors.saveWarning, saveWarningMessage: errors._warning });
            } else {
              this.moveToState(closed);
            }
          } catch (error) {
            this.moveToState(saveError, { saveError: true, saveErrorMessage: error.message });
          }
        };

        this.setState(
          (prevState) => ({
            state: saving,
            data: { ...prevState.data, formErrorMessage: null },
          }),
          afterSetLocal
        );

        break;
      case updating:
        this.setState(
          (prevState) => ({
            state: updating,
            data: {
              ...prevState.data,
              formErrorMessage: null,
            },
          }),
          () => {
            this.moveToState(open);
          }
        );
        break;
      case saveError:
        this.setState((prevState) => ({
          state: saveError,
          data: { ...prevState.data, ...payload },
        }));
        break;
      case saveWarning:
        this.setState((prevState) => ({
          state: saveWarning,
          data: { ...prevState.data, ...payload },
        }));
        break;
      case formValidationError:
        this.setState((prevState) => ({
          state: formValidationError,
          data: { ...prevState.data },
        }));
        break;
      default:
        console.log(`<${state}> is not a valid state for ${this.props.entityType} modal`);
    }
  };

  save = () => {
    this.moveToState(states.saving);
  };

  closeModal = () => {
    this.moveToState(states.closed);
  };

  openModal = () => {
    this.moveToState(states.open);
  };

  toggleFunc = () => {
    const { state } = this.state;

    if (state !== states.saving) {
      return this.moveToState(states.closed);
    }

    return () => {};
  };

  /**
   * Need this to share what an error means to rendering the body and footer in the modal.
   * This differs from this.props.getErrors in that it is used to tell if the button should be shown or not.
   */
  shouldRenderError = () => {
    return this.state.data.saveError || null;
  };

  shouldRenderWarning = () => {
    return this.state.data.saveWarning || null;
  };

  renderErrorBody = () => {
    const errorMessage = this.state.data.saveErrorMessage || '';

    return (
      <ModalBody>
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
          <WarningAmber sx={bodyIconSx('error.main')} />
          <div>
            <Typography sx={{ mb: 2 }}>
              Error saving {`${this.props.entityType}`}. Please close and try again.
            </Typography>
            <Typography sx={{ mb: 1 }}>Message:</Typography>
            <Alert color='danger'>{errorMessage}</Alert>
          </div>
        </Stack>
      </ModalBody>
    );
  };

  renderWarningBody = () => {
    return (
      <ModalBody>
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
          <WarningAmber sx={bodyIconSx('warning.main')} />
          <div>
            <Typography sx={{ mb: 2 }}>Unexpected behavior while saving {this.props.entityType}.</Typography>
            <Alert color='warning'>{this.state.data.saveWarningMessage || ''}</Alert>
          </div>
        </Stack>
      </ModalBody>
    );
  };

  renderNormalBody = () => {
    return (
      <ModalBody>
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
          <CheckCircleOutlined sx={bodyIconSx('success.main')} />
          <div>
            <Typography variant='h6'>{this.props.content && this.props.content.name}</Typography>
            <Typography>
              You are about to {this.props.isEditMode ? 'edit this ' : 'create a new '}
              {this.props.entityType}:
            </Typography>
            <Typography variant='h6'>{this.props.entityName}</Typography>
          </div>
        </Stack>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 2, fontStyle: 'italic' }}>
          This may take a moment to complete.
        </Typography>
      </ModalBody>
    );
  };

  renderBody = () => {
    if (this.shouldRenderError()) {
      return this.renderErrorBody();
    }
    if (this.shouldRenderWarning()) {
      return this.renderWarningBody();
    }
    return this.renderNormalBody();
  };

  getModalTone = () => {
    if (this.shouldRenderError()) {
      return 'danger';
    }
    if (this.shouldRenderWarning()) {
      return 'warning';
    }
    return 'info';
  };

  renderErrorFooter = () => {
    return (
      <ModalFooter>
        <Button color='secondary' onClick={this.closeModal}>
          Close
        </Button>
      </ModalFooter>
    );
  };

  renderNormalFooter = () => {
    const { state } = this.state;
    const isSaving = state === states.saving;

    return (
      <ModalFooter>
        <Button color='info' style={{ color: 'white' }} disabled={isSaving} onClick={this.save}>
          {isSaving ? 'Processing' : 'Publish'}
        </Button>
        <Button color='secondary' disabled={isSaving} onClick={this.closeModal}>
          {isSaving ? 'Processing' : 'Cancel'}
        </Button>
      </ModalFooter>
    );
  };

  renderFooter = () => {
    if (this.shouldRenderError() || this.shouldRenderWarning()) {
      return this.renderErrorFooter();
    } else {
      return this.renderNormalFooter();
    }
  };

  render() {
    const errors = this.props.getErrors();
    const isDisabled = errors.invalid;

    return (
      <div>
        <Button
          color='info'
          onClick={this.openModal}
          disabled={isDisabled}
          // sx rather than an inline style: the StickyActionBar resets float/margins on the
          // buttons it lays out, and an inline style outranks that reset.
          sx={{ color: 'white', float: 'right', ml: 2, mb: 2 }}
          className={this.props.className}
        >
          Publish {`${this.props.entityType}`}
        </Button>
        <Modal
          isOpen={this.state.data.isOpen}
          toggle={this.toggleFunc}
          onClosed={this.closeModal}
          tone={this.getModalTone()}
        >
          <ModalHeader toggle={this.closeModal}>Publish {`${this.props.entityType}`}</ModalHeader>
          {this.renderBody()}
          {this.renderFooter()}
        </Modal>
      </div>
    );
  }
}

const mapState = (state) => {
  return {};
};

export default connect(mapState, { resetModal })(SaveEntityModal);
